import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { trackEvent } from '@/lib/analytics'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { LocationsSection } from '@/components/sections/locations-section'
import { TableOfContents } from '@/components/sections/table-of-contents'
import { CTA } from '@/components/sections/cta'
import { CTA15Min } from '@/components/sections/cta-15min'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Flower2, Baby, Heart, Moon, Leaf, AudioWaveform } from 'lucide-react'

const tocItems = [
  { id: 'who-renee-is', title: 'Who Renee Is' },
  { 
    id: 'areas-of-focus', 
    title: 'Areas of Focus',
    children: [
      { id: 'fertility-ivf', title: 'Fertility & IVF Support' },
      { id: 'pregnancy-postpartum', title: 'Pregnancy & Postpartum Care' },
      { id: 'menstrual-hormonal', title: 'Menstrual & Hormonal Health' },
      { id: 'stress-sleep-digestion', title: 'Stress, Sleep & Digestion' },
      { id: 'ongoing-womens-health', title: 'Ongoing Women\'s Health & Long-Term Care' },
      { id: 'other-concerns', title: 'Other Concerns' },
    ]
  },
  { id: 'how-renee-works', title: 'How Renee Works' },
  { id: 'where-you-can-see-renee', title: 'Where You Can See Renee' },
  { id: 'qualifications', title: 'Qualifications & Registration' },
  { id: 'personal-note', title: 'A Personal Note from Renee' },
]

export default function DrReneeKnottPage() {
  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Dr Renee Knott | Acupuncture & Chinese Medicine | Raintree Chinese Medicine</title>
        <meta name="description" content="Dr Renee Knott (Chinese Med.) is an AHPRA-registered acupuncturist and Chinese herbalist with around 20 years' clinical experience in women's health, fertility and pregnancy, practising at Raintree Chinese Medicine in Noosa and Maroochydore." />
        <meta name="keywords" content="Renee Knott, Renee Knott acupuncture, Dr Renee Knott, acupuncture Noosa, acupuncture Maroochydore, Chinese medicine Sunshine Coast, fertility acupuncture, women's health acupuncture, Raintree Chinese Medicine, AHPRA registered acupuncturist" />
        <link rel="canonical" href="https://raintreechinesemedicine.com.au/dr-renee-knott" />
        <meta property="og:title" content="Dr Renee Knott | Acupuncture & Chinese Medicine | Raintree Chinese Medicine" />
        <meta property="og:description" content="Dr Renee Knott (Chinese Med.) is an AHPRA-registered acupuncturist and Chinese herbalist with around 20 years' clinical experience in women's health, fertility and pregnancy, practising at Raintree Chinese Medicine in Noosa and Maroochydore." />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/dr-renee-knott" />
        <meta property="og:image" content="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dr Renee Knott | Acupuncture & Chinese Medicine | Raintree Chinese Medicine" />
        <meta name="twitter:description" content="Dr Renee Knott (Chinese Med.) is an AHPRA-registered acupuncturist and Chinese herbalist with around 20 years' clinical experience in women's health, fertility and pregnancy, practising at Raintree Chinese Medicine in Noosa and Maroochydore." />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: 'Dr Renee Knott',
            alternateName: 'Renee Knott',
            jobTitle: 'Acupuncturist and Chinese Medicine Herbalist',
            description: 'AHPRA-registered acupuncturist and Chinese herbalist with around 20 years of clinical experience specializing in women\'s health, fertility, pregnancy and hormonal health. Practising at Raintree Chinese Medicine in Noosa and Maroochydore on the Sunshine Coast.',
            image: 'https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE',
            url: 'https://raintreechinesemedicine.com.au/dr-renee-knott',
            sameAs: [
              'https://raintreechinesemedicine.com.au'
            ],
            knowsAbout: [
              'Acupuncture',
              'Chinese Medicine',
              'Women\'s Health',
              'Fertility Treatment',
              'Pregnancy Care',
              'Hormonal Health',
              'IVF Support',
              'Menstrual Health',
              'Perimenopause',
              'Stress',
              'Sleep',
              'Digestion',
              'Ongoing Women\'s Health',
              'Long-Term Care',
              'Nervous System Support',
              'Nervous System Balance',
              'Cancer Support',
              'Cancer Care',
            ],
            alumniOf: {
              '@type': 'EducationalOrganization',
              name: 'Victoria University',
              description: 'Bachelor of Chinese Medicine (Honours) – Acupuncture & Chinese Herbal Medicine'
            },
            memberOf: [
              {
                '@type': 'Organization',
                name: 'Australian Traditional-Medicine Society (ATMS)'
              },
              {
                '@type': 'Organization',
                name: 'AHPRA (Australian Health Practitioner Regulation Agency)'
              },
              {
                '@type': 'Organization',
                name: 'Chinese Medicine Board of Australia (CMBA)'
              }
            ],
            worksFor: {
              '@type': 'MedicalBusiness',
              name: 'Raintree Chinese Medicine',
              address: [
                {
                  '@type': 'PostalAddress',
                  streetAddress: '31 Burgess Drive',
                  addressLocality: 'Tewantin',
                  addressRegion: 'QLD',
                  postalCode: '4565',
                  addressCountry: 'AU'
                },
                {
                  '@type': 'PostalAddress',
                  streetAddress: 'Ground Level 5/55 Plaza Parade',
                  addressLocality: 'Maroochydore',
                  addressRegion: 'QLD',
                  postalCode: '4558',
                  addressCountry: 'AU'
                }
              ],
              areaServed: [
                'Noosa',
                'Noosa Heads',
                'Noosaville',
                'Tewantin',
                'Maroochydore',
                'Sunshine Coast',
                'Buderim',
                'Bli Bli',
                'Cotton Tree',
                'Mooloolaba'
              ]
            },
            hasCredential: {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'Professional License',
              recognizedBy: {
                '@type': 'Organization',
                name: 'AHPRA - Australian Health Practitioner Regulation Agency'
              }
            }
          })}
        </script>
      </Helmet>
      <h1 className="sr-only">Dr Renee Knott - Acupuncturist & Chinese Medicine Herbalist at Raintree Chinese Medicine</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-2 text-white">
                Dr Renee Knott - Acupuncture & Chinese Medicine
              </h1>
              <p className="text-white/90 mb-2 text-sm">
                AHPRA Registered Acupuncturist & Chinese Herbalist at Raintree Chinese Medicine
              </p>
              <p className="text-white/90 mb-6 text-sm">
                Women's Health · Fertility · Pregnancy · Hormonal Health
              </p>
              <div className="flex justify-center mb-6">
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE"
                  alt="Dr Renee Knott - AHPRA registered acupuncturist and Chinese medicine herbalist at Raintree Chinese Medicine, Noosa and Maroochydore"
                  className="aspect-square object-cover w-full max-w-sm shadow-lg rounded-lg"
                />
              </div>
              <p className="text-white/90 text-sm text-left max-w-2xl mx-auto mb-4">
                Dr Renee Knott (Chinese Med) is an AHPRA and CMRBA registered Acupuncturist and Chinese Medicine Herbalist with over 20 years of clinical experience. Renee Knott acupuncture services include Chinese herbal medicine with a particular focus on women's health, fertility, pregnancy and long-term care.
              </p>
              <p className="text-white/90 text-sm text-left max-w-2xl mx-auto">
                Dr Renee Knott practises acupuncture at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast.
              </p>
            </div>

            {/* Desktop: Side-by-side layout - text left, image right */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-2 text-white">
                  Dr Renee Knott - Acupuncture & Chinese Medicine
                </h1>
                <p className="text-white/90 mb-2 text-base lg:text-lg">
                  AHPRA Registered Acupuncturist & Chinese Herbalist at Raintree Chinese Medicine
                </p>
                <p className="text-white/90 mb-4 text-base lg:text-lg">
                  Women's Health · Fertility · Pregnancy · Hormonal Health
                </p>
                <p className="text-white/90 text-base lg:text-lg mb-4">
                  Dr Renee Knott (Chinese Med) is an AHPRA and CMRBA registered Acupuncturist and Chinese Medicine Herbalist with over 20 years of clinical experience. Renee Knott acupuncture services include Chinese herbal medicine with a particular focus on women's health, fertility, pregnancy and long-term care.
                </p>
                <p className="text-white/90 text-base lg:text-lg">
                  Dr Renee Knott practises acupuncture at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast.
                </p>
              </div>
              <div className="flex justify-center">
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE"
                  alt="Dr Renee Knott - AHPRA registered acupuncturist and Chinese medicine herbalist at Raintree Chinese Medicine, Noosa and Maroochydore"
                  className="aspect-square object-cover w-full max-w-md shadow-lg rounded-lg"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who Renee Is Section */}
      <section id="who-renee-is" className="py-20 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="md:hidden">
              <div className="flex justify-center mb-6">
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee-Knott.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLUtub3R0LnBuZyIsImlhdCI6MTc2MjI5ODM3NSwiZXhwIjo0ODg0MzYyMzc1fQ.oWb0fbHoIsvcA0Jc2IAok36HiwggiQ3bs_1iY-jpBXU"
                  alt="Dr Renee Knott - AHPRA registered acupuncturist and Chinese medicine herbalist"
                  className="aspect-square object-cover w-full max-w-sm shadow-lg"
                />
              </div>
              <div className="pl-4 max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl mb-4 mt-8 text-[#3f4141] text-center">
                  Who Renee Is
                </h2>
                <p className="text-gray-600 mb-4 text-center">
                  Renee Knott began studying Chinese medicine more than 25 years ago and continues to deepen her knowledge to this day. She has worked in private practice for over 15 years in Melbourne and, more recently, spent five years at Bloom Chinese Medicine on the Sunshine Coast, one of Queensland's busiest fertility-focused Chinese medicine clinics. That experience of working closely with complex fertility, IVF and women's health presentations continues to inform her work at Raintree Chinese Medicine, where she now practises acupuncture in Noosa and Maroochydore.
                </p>
                <p className="text-gray-600 mb-4 text-center">
                  Whilst Renee works with a wide variety of health concerns, she has a particular passion for women's health. She enjoys supporting women through each phase of life – from first periods and cycle changes, through fertility and pregnancy, to postpartum recovery, perimenopause and beyond.
                </p>
                <p className="text-gray-600 text-center">
                  Renee is a life-long student of Chinese medicine. She has trained with some of the field's most respected teachers, including Arnaud Versluys and Sharon Weizenbaum, and brings that depth of study into everyday treatments in a way that is practical and down-to-earth.
                </p>
              </div>
            </div>

            {/* Desktop: 2-column layout - image left, text right */}
            <div className="hidden md:grid md:grid-cols-2 md:gap-8 lg:gap-12 md:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex items-center justify-center"
              >
                <img
                  src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee-Knott.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLUtub3R0LnBuZyIsImlhdCI6MTc2MjI5ODM3NSwiZXhwIjo0ODg0MzYyMzc1fQ.oWb0fbHoIsvcA0Jc2IAok36HiwggiQ3bs_1iY-jpBXU"
                  alt="Dr Renee Knott - AHPRA registered acupuncturist and Chinese medicine herbalist"
                  className="aspect-square md:aspect-auto object-cover w-full md:w-auto md:h-full md:max-h-[600px] shadow-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex flex-col justify-center"
              >
                <div className="max-w-xl">
                  <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-left">
                    Who Renee Is
                  </h2>
                  <p className="text-gray-600 mb-4 text-left">
                    Renee Knott began studying Chinese medicine more than 25 years ago and continues to deepen her knowledge to this day. She has worked in private practice for over 15 years in Melbourne and, more recently, spent five years at Bloom Chinese Medicine on the Sunshine Coast, one of Queensland's busiest fertility-focused Chinese medicine clinics. That experience of working closely with complex fertility, IVF and women's health presentations continues to inform her work at Raintree Chinese Medicine, where she now practises acupuncture in Noosa and Maroochydore.
                  </p>
                  <p className="text-gray-600 mb-4 text-left">
                    Whilst Renee works with a wide variety of health concerns, she has a particular passion for women's health. She enjoys supporting women through each phase of life – from first periods and cycle changes, through fertility and pregnancy, to postpartum recovery, perimenopause and beyond.
                  </p>
                  <p className="text-gray-600 text-left">
                    Renee is a life-long student of Chinese medicine. She has trained with some of the field's most respected teachers, including Arnaud Versluys and Sharon Weizenbaum, and brings that depth of study into everyday treatments in a way that is practical and down-to-earth.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Areas of Focus Section */}
      <section id="areas-of-focus" className="py-20 bg-gray-50 px-4 lg:px-0">
        <div className="container mx-auto px-4 sm:w-2/3 md:w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl mb-4 text-[#3f4141] max-w-[900px] mx-auto">
              Areas of Focus
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Renee's work centres on a few main areas. The same appointment types are used across all of these – the difference lies in how treatment is tailored to each person.
            </p>
          </motion.div>

          {/* Cards Grid - 2 columns on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto place-items-center md:place-items-stretch">
            {/* Fertility & IVF Support Card */}
            <motion.div
              key="fertility-ivf"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="w-full"
            >
              <Card id="fertility-ivf" className="h-full border border-gray-200 shadow-sm flex flex-col">
                <CardHeader>
                  <Flower2 className="h-8 w-8 text-[#DBB981] mb-4" />
                  <CardTitle className="text-[#3f4141] font-light text-lg md:text-xl">
                    Fertility & IVF Support
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-4 text-left">
                    Renee works with people who are trying to conceive, both naturally and with IVF/ICSI. Appointments may include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-left">
                    <li>Looking at cycles, bleeding patterns and symptoms over the month</li>
                    <li>Supporting stress, sleep and digestion during fertility treatment</li>
                    <li>Offering acupuncture and, where appropriate, Chinese herbal medicine designed to sit alongside medical fertility care</li>
                  </ul>
                  <p className="text-gray-600 mb-4 text-left">
                    The aim is to help people feel steadier, more supported and more informed during what can be an intense process.
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      to="/what-we-help-with/fertility-preconception"
                      state={{ fromDrReneeKnottPage: true }}
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                    >
                      Learn more
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Pregnancy & Postpartum Care Card */}
            <motion.div
              key="pregnancy-postpartum"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-full"
            >
              <Card id="pregnancy-postpartum" className="h-full border border-gray-200 shadow-sm flex flex-col">
                <CardHeader>
                  <Baby className="h-8 w-8 text-[#DBB981] mb-4" />
                  <CardTitle className="text-[#3f4141] font-light text-lg md:text-xl">
                    Pregnancy & Postpartum Care
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-4 text-left">
                    Renee provides care through all trimesters and into the postpartum months. Treatment may help with:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-left">
                    <li>Common pregnancy symptoms such as nausea, sleep disruption, discomfort and anxiety</li>
                    <li>Late-pregnancy comfort and preparation for birth</li>
                    <li>Postnatal recovery, energy and emotional adjustment in the "fourth trimester"</li>
                  </ul>
                  <p className="text-gray-600 mb-4 text-left">
                    Treatment is always tailored to stage of pregnancy and designed to fit with the advice of each person's midwife, GP or obstetric team.
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      to="/what-we-help-with/pregnancy-postpartum"
                      state={{ fromDrReneeKnottPage: true }}
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                    >
                      Learn more
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Menstrual & Hormonal Health Card */}
            <motion.div
              key="menstrual-hormonal"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="w-full"
            >
              <Card id="menstrual-hormonal" className="h-full border border-gray-200 shadow-sm flex flex-col">
                <CardHeader>
                  <Heart className="h-8 w-8 text-[#DBB981] mb-4" />
                  <CardTitle className="text-[#3f4141] font-light text-lg md:text-xl">
                    Menstrual & Hormonal Health
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-4 text-left">
                    Many people see Renee for menstrual and hormonal concerns, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-left">
                    <li>Painful, heavy or irregular periods</li>
                    <li>PMS/PMDD and mood changes around the cycle</li>
                    <li>Perimenopausal symptoms such as sleep disturbance, hot flushes, mood shifts or cycle changes</li>
                  </ul>
                  <p className="text-gray-600 mb-4 text-left">
                    Appointments are a space to make sense of what is happening, connect hormones with stress, sleep and digestion, and work towards cycles that feel more predictable and manageable.
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      to="/what-we-help-with/menstrual-hormonal-health"
                      state={{ fromDrReneeKnottPage: true }}
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                    >
                      Learn more
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Stress, Sleep & Digestion Card */}
            <motion.div
              key="stress-sleep-digestion"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="w-full"
            >
              <Card id="stress-sleep-digestion" className="h-full border border-gray-200 shadow-sm flex flex-col">
                <CardHeader>
                  <Moon className="h-8 w-8 text-[#DBB981] mb-4" />
                  <CardTitle className="text-[#3f4141] font-light text-lg md:text-xl">
                    Stress, Sleep & Digestion
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-4 text-left">
                    Underneath many symptoms, there is a nervous system and a digestive system that need attention. Renee often works with:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-left">
                    <li>Feeling constantly "on", wired or depleted</li>
                    <li>Difficulty falling asleep or staying asleep</li>
                    <li>IBS-type symptoms, bloating or unsettled digestion linked to stress</li>
                    <li>The general sense of burnout that can sit underneath everything else</li>
                  </ul>
                  <p className="text-gray-600 mb-4 text-left">
                    Sessions are a chance to pause, regulate and reset, while gently rebuilding resilience over time.
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      to="/what-we-help-with/stress-sleep-digestion"
                      state={{ fromDrReneeKnottPage: true }}
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                    >
                      Learn more
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Ongoing Women's Health & Long-Term Care Card */}
            <motion.div
              key="ongoing-womens-health"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="w-full"
            >
              <Card id="ongoing-womens-health" className="h-full border border-gray-200 shadow-sm flex flex-col">
                <CardHeader>
                  <Leaf className="h-8 w-8 text-[#DBB981] mb-4" />
                  <CardTitle className="text-[#3f4141] font-light text-lg md:text-xl">
                    Ongoing Women's Health & Long-Term Care
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-4 text-left">
                    Some people see Renee for a short, focused period. Many others choose to stay over several life stages – for example, moving from cycle issues into preconception care, pregnancy, postpartum recovery and later perimenopause.
                  </p>
                  <p className="text-gray-600 mb-4 text-left">
                    Renee values continuity. She aims to be a steady presence over time, remembering each person's story and adjusting treatment as life and health needs change.
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      to="/what-we-help-with/womens-health-long-term-care"
                      state={{ fromDrReneeKnottPage: true }}
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                    >
                      Learn more
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Other Concerns Card */}
            <motion.div
              key="other-concerns"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="w-full"
            >
              <Card id="other-concerns" className="h-full border border-gray-200 shadow-sm flex flex-col">
                <CardHeader>
                  <AudioWaveform className="h-8 w-8 text-[#DBB981] mb-4" />
                  <CardTitle className="text-[#3f4141] font-light text-lg md:text-xl">
                    Other Concerns
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-4 text-left">
                    While Raintree's main focus is women's health, fertility and hormones, some people also come for support with:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-left">
                    <li>Tension-related pain</li>
                    <li>Chronic headaches or migraines</li>
                    <li>Complex symptom patterns</li>
                    <li>Cancer care and support</li>
                  </ul>
                  <p className="text-gray-600 mb-4 text-left">
                    We recognize that health concerns don't always fit into neat categories. We work with you to address your unique situation.
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      to="/what-we-help-with/other-concerns"
                      state={{ fromDrReneeKnottPage: true }}
                      className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-sm"
                    >
                      Learn more
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA 15 Min Section */}
      <CTA15Min onBook={openBooking} />

      {/* How Renee Works Section */}
      <section id="how-renee-works" className="py-20 bg-gray-50 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              How Renee Works
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-8 text-left text-base">
                Renee's style is calm, steady and unhurried. She takes time to listen carefully, ask detailed questions and explain what she is seeing in clear, everyday language.
              </p>
              
              <div className="mb-8">
                <h3 className="text-xl md:text-xl mb-4 text-[#3f4141] text-left text-base">
                  In treatment planning, she:
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                  <li>Works collaboratively with GPs, specialists, midwives and IVF clinics</li>
                  <li>Designs plans that are realistic for real life – work, family and finances</li>
                  <li>Treats the nervous system and digestion as central to many presentations, not an afterthought</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl md:text-xl mb-4 text-[#3f4141] text-left text-base">
                  At each appointment there is time to:
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 text-left">
                  <li>Check in on how things have been since the last visit</li>
                  <li>Review any changes in symptoms, energy, sleep, mood or digestion</li>
                  <li>Adjust treatment based on where someone is in their cycle, fertility or pregnancy timeline</li>
                  <li>Make small, practical suggestions that fit into daily life</li>
                </ul>
              </div>

              <p className="text-gray-600 mt-8 text-left">
                The goal is not a quick fix, but steady, sustainable change over time – with someone experienced in your corner.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Where You Can See Renee Section */}
      <section id="where-you-can-see-renee" className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <LocationsSection onBook={(locationName) => {
              trackEvent('book_appointment_click', { location: `dr_renee_locations_${locationName.toLowerCase()}` })
              openBooking(`dr_renee_locations_${locationName.toLowerCase()}`)
            }} />
          </motion.div>
        </div>
      </section>

      {/* Qualifications & Registration Section */}
      <section id="qualifications" className="py-20 bg-gray-50 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Qualifications & Registration
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left text-base font-medium">
                Dr Renee Knott's training and registrations include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Bachelor of Chinese Medicine (Honours) – Acupuncture & Chinese Herbal Medicine, Victoria University (2005/2006)</li>
                <li>Diplomate – ICEAM (Institute of Classics in East Asian Medicine)</li>
                <li>Graduate Mentorship Program – White Pine Institute</li>
                <li>Women's Great Turning – White Pine Institute</li>
                <li>Clinical Observation Internship – Kyung Hee University, Seoul (2005)</li>
                <li>Diploma in Traditional Thai Massage (2003)</li>
                <li>Member – ATMS (Australian Traditional-Medicine Society)</li>
                <li>Registered with AHPRA and the Chinese Medicine Board of Australia (CMBA)</li>
                <li>HICAPS / health fund rebates available for most major funds (depending on individual cover)</li>
              </ul>
              <p className="text-gray-600 mt-6 text-left">
                Dr Renee Knott provides acupuncture and Chinese medicine services at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast, Queensland.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* A Personal Note from Renee Section */}
      <section id="personal-note" className="py-20 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              A Personal Note from Renee
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left">
                Renee now calls Noosa home, where she lives with her partner and two children. Her ideal day includes an early surf with the family, a slow afternoon and dinner with good food, good music and great company.
              </p>
              <p className="text-gray-600 mb-8 text-left">
                She has maintained a daily yoga practice for almost 30 years. For her, these practices are part of the same thread that runs through her clinical work: presence, curiosity and care.
              </p>
              <p className="text-gray-600 text-left">
                If you would like to explore whether working together is the right fit, you are welcome to book an{' '}
                <button
                  onClick={() => openBooking('dr_renee_initial_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  initial consultation
                </button>
                {' '}or a{' '}
                <button
                  onClick={() => openBooking('dr_renee_free_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  free 15-minute phone consult
                </button>
                {' '}to talk through what you are looking for and how acupuncture and Chinese medicine may be able to support you.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />

    </main>
  )
}
